import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { documentoAPI, empresaAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
    QrCode,
    Camera,
    X,
    CheckCircle,
    XCircle,
    Loader2,
    AlertTriangle,
    ArrowLeft,
    Truck,
    Building,
    Package,
} from 'lucide-react';

export default function EscanerQRPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { setDarkMode } = useTheme();
    const [scanning, setScanning] = useState(false);
    const [scannedDoc, setScannedDoc] = useState(null);
    const [loading, setLoading] = useState(false);
    const [empresa, setEmpresa] = useState(null);
    const [actionType, setActionType] = useState(null);
    const [motivosSeleccionados, setMotivosSeleccionados] = useState([]);
    const [observacion, setObservacion] = useState('');
    const [processing, setProcessing] = useState(false);
    const scannerRef = useRef(null);
    const html5QrCodeRef = useRef(null);

    useEffect(() => {
        setDarkMode();
        loadEmpresa();
        return () => {
            stopScanner();
        };
    }, []);

    const loadEmpresa = async () => {
        if (user?.empresa_id) {
            try {
                const response = await empresaAPI.getById(user.empresa_id);
                setEmpresa(response.data);
            } catch (err) {
                console.error('Error loading empresa:', err);
            }
        }
    };

    const startScanner = () => {
        setScanning(true);
        setScannedDoc(null);

        setTimeout(() => {
            if (scannerRef.current && !html5QrCodeRef.current) {
                html5QrCodeRef.current = new Html5QrcodeScanner(
                    "qr-reader",
                    { 
                        fps: 10, 
                        qrbox: { width: 250, height: 250 },
                        aspectRatio: 1.0,
                    },
                    false
                );

                html5QrCodeRef.current.render(onScanSuccess, onScanFailure);
            }
        }, 100);
    };

    const stopScanner = () => {
        if (html5QrCodeRef.current) {
            html5QrCodeRef.current.clear().catch(err => console.log('Scanner clear error:', err));
            html5QrCodeRef.current = null;
        }
        setScanning(false);
    };

    const onScanSuccess = async (decodedText) => {
        stopScanner();
        setLoading(true);

        try {
            // Extract document ID from URL or direct ID
            let docId = decodedText;
            
            // If it's a URL, extract the ID
            if (decodedText.includes('/documento/')) {
                const parts = decodedText.split('/documento/');
                docId = parts[parts.length - 1].split('?')[0];
            } else if (decodedText.includes('/qr/')) {
                const parts = decodedText.split('/qr/');
                docId = parts[parts.length - 1].split('?')[0];
            }

            const response = await documentoAPI.getById(docId);
            setScannedDoc(response.data);
            
            if (response.data.estado !== 'autorizado') {
                toast.warning('Estado no válido', {
                    description: `Esta guía está en estado: ${response.data.estado}`,
                });
            }
        } catch (err) {
            toast.error('Error al buscar guía', {
                description: 'No se encontró la guía escaneada',
            });
            setScannedDoc(null);
        } finally {
            setLoading(false);
        }
    };

    const onScanFailure = (error) => {
        // Silently ignore scan failures (normal during scanning)
    };

    const openActionDialog = (type) => {
        setActionType(type);
        setMotivosSeleccionados([]);
        setObservacion('');
    };

    const closeActionDialog = () => {
        setActionType(null);
        setMotivosSeleccionados([]);
        setObservacion('');
    };

    const toggleMotivo = (motivo) => {
        setMotivosSeleccionados((prev) =>
            prev.includes(motivo)
                ? prev.filter((m) => m !== motivo)
                : [...prev, motivo]
        );
    };

    const handleDespacho = async () => {
        if (!scannedDoc) return;

        if (actionType === 'rechazar') {
            if (motivosSeleccionados.length === 0) {
                toast.error('Debe seleccionar al menos un motivo de rechazo');
                return;
            }
            if (!observacion.trim()) {
                toast.error('Debe ingresar una observación');
                return;
            }
        }

        setProcessing(true);
        try {
            await documentoAPI.despachar(scannedDoc.id, {
                documento_id: scannedDoc.id,
                estado: actionType === 'despachar' ? 'despachado' : 'rechazado',
                motivos_rechazo: actionType === 'rechazar' ? motivosSeleccionados : [],
                observacion: observacion || null,
            });

            toast.success(
                actionType === 'despachar'
                    ? 'Guía despachada exitosamente'
                    : 'Guía rechazada en punto de salida',
                { description: `Folio: ${scannedDoc.folio}` }
            );

            closeActionDialog();
            setScannedDoc(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al procesar la guía');
        } finally {
            setProcessing(false);
        }
    };

    const motivosRechazo = empresa?.motivos_rechazo || [
        'Sello roto',
        'Carga incompleta',
        'Documentación incorrecta',
        'Vehículo no autorizado',
        'Personal no identificado',
    ];

    return (
        <div className="min-h-screen bg-background security-view">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b-2 border-border h-16">
                <div className="flex items-center justify-between h-full px-4">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/guias/punto-salida')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center gap-2">
                            <QrCode className="h-6 w-6 text-primary" />
                            <h1 className="font-heading font-black text-lg uppercase tracking-tight">
                                Escáner QR
                            </h1>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20 pb-4 px-4">
                {!scanning && !scannedDoc && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <div className="p-8 rounded-sm bg-card border-2 border-dashed border-border text-center max-w-sm">
                            <QrCode className="h-16 w-16 mx-auto mb-4 text-primary" />
                            <h2 className="font-heading font-bold text-xl uppercase mb-2">
                                Escanear Guía
                            </h2>
                            <p className="text-sm text-muted-foreground mb-6">
                                Escanee el código QR de la guía para verificar y despachar
                            </p>
                            <Button
                                onClick={startScanner}
                                className="w-full security-mobile-btn bg-primary text-primary-foreground"
                                data-testid="start-scanner-btn"
                            >
                                <Camera className="h-6 w-6 mr-2" />
                                INICIAR CÁMARA
                            </Button>
                        </div>
                    </div>
                )}

                {scanning && (
                    <div className="max-w-md mx-auto">
                        <Card className="border-2">
                            <CardHeader className="card-industrial-header flex-row items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <Camera className="h-5 w-5" />
                                    Escaneando...
                                </CardTitle>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={stopScanner}
                                    data-testid="stop-scanner-btn"
                                >
                                    <X className="h-5 w-5" />
                                </Button>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div 
                                    id="qr-reader" 
                                    ref={scannerRef}
                                    className="w-full"
                                    style={{ minHeight: '300px' }}
                                />
                            </CardContent>
                        </Card>
                        <p className="text-center text-sm text-muted-foreground mt-4">
                            Apunte la cámara al código QR de la guía
                        </p>
                    </div>
                )}

                {loading && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh]">
                        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                        <p className="text-muted-foreground">Buscando guía...</p>
                    </div>
                )}

                {scannedDoc && !loading && (
                    <div className="max-w-md mx-auto space-y-4">
                        <Card className="border-2" data-testid="scanned-doc-card">
                            <CardHeader className="card-industrial-header">
                                <div className="flex items-center justify-between">
                                    <span className="folio-display text-lg">{scannedDoc.folio}</span>
                                    <StatusBadge estado={scannedDoc.estado} />
                                </div>
                            </CardHeader>
                            <CardContent className="p-4 space-y-4">
                                {/* Vehicle & Destination */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-3 bg-muted/50 rounded-sm text-center">
                                        <Truck className="h-6 w-6 mx-auto mb-2 text-primary" />
                                        <p className="font-mono text-xl font-bold">{scannedDoc.patente}</p>
                                        {scannedDoc.vehiculo_info && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {scannedDoc.vehiculo_info.marca}
                                            </p>
                                        )}
                                    </div>
                                    <div className="p-3 bg-muted/50 rounded-sm text-center">
                                        <Building className="h-6 w-6 mx-auto mb-2 text-primary" />
                                        <p className="font-bold text-sm truncate">
                                            {scannedDoc.destino_info?.razon_social || 'Sin nombre'}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {scannedDoc.rut_destino}
                                        </p>
                                    </div>
                                </div>

                                {/* Items */}
                                {scannedDoc.items && scannedDoc.items.length > 0 && (
                                    <div className="p-3 bg-muted/30 rounded-sm">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Package className="h-4 w-4" />
                                            <span className="text-sm font-bold uppercase">
                                                Carga ({scannedDoc.items.length})
                                            </span>
                                        </div>
                                        <ul className="text-sm space-y-1">
                                            {scannedDoc.items.slice(0, 3).map((item, idx) => (
                                                <li key={idx}>
                                                    • {item.descripcion} - {item.cantidad} {item.unidad}
                                                </li>
                                            ))}
                                            {scannedDoc.items.length > 3 && (
                                                <li className="text-muted-foreground">
                                                    + {scannedDoc.items.length - 3} más...
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                )}

                                {/* Actions */}
                                {scannedDoc.estado === 'autorizado' ? (
                                    <div className="grid grid-cols-2 gap-3 pt-2">
                                        <Button
                                            onClick={() => openActionDialog('despachar')}
                                            className="security-mobile-btn bg-success text-success-foreground hover:bg-success/90"
                                            data-testid="despachar-btn"
                                        >
                                            <CheckCircle className="h-6 w-6" />
                                            DESPACHAR
                                        </Button>
                                        <Button
                                            onClick={() => openActionDialog('rechazar')}
                                            variant="outline"
                                            className="security-mobile-btn border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                            data-testid="rechazar-btn"
                                        >
                                            <XCircle className="h-6 w-6" />
                                            RECHAZAR
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="p-4 bg-warning/10 border border-warning/30 rounded-sm">
                                        <div className="flex items-center gap-2 text-warning">
                                            <AlertTriangle className="h-5 w-5" />
                                            <span className="font-medium">
                                                Esta guía no puede ser despachada
                                            </span>
                                        </div>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            Estado actual: {scannedDoc.estado}
                                        </p>
                                    </div>
                                )}

                                {/* Scan Another */}
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setScannedDoc(null);
                                        startScanner();
                                    }}
                                    className="w-full border-2 mt-4"
                                    data-testid="scan-another-btn"
                                >
                                    <QrCode className="h-4 w-4 mr-2" />
                                    Escanear otra guía
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </main>

            {/* Action Dialog */}
            <Dialog open={!!actionType} onOpenChange={() => closeActionDialog()}>
                <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">
                            {actionType === 'despachar' ? 'Confirmar Despacho' : 'Rechazar en Punto de Salida'}
                        </DialogTitle>
                        <DialogDescription>
                            {scannedDoc?.folio} - {scannedDoc?.patente}
                        </DialogDescription>
                    </DialogHeader>

                    <ScrollArea className="flex-1 pr-4">
                        <div className="space-y-4 py-4">
                            {actionType === 'despachar' ? (
                                <>
                                    <div className="p-4 bg-success/10 border border-success/30 rounded-sm">
                                        <div className="flex items-center gap-3">
                                            <CheckCircle className="h-8 w-8 text-success" />
                                            <div>
                                                <p className="font-bold text-success">Verificación Completa</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Confirme que la carga física corresponde a la guía
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="input-label">Observación (opcional)</Label>
                                        <Textarea
                                            value={observacion}
                                            onChange={(e) => setObservacion(e.target.value)}
                                            placeholder="Notas adicionales..."
                                            className="min-h-[80px] border-2"
                                            data-testid="observacion-input"
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-sm">
                                        <div className="flex items-center gap-3">
                                            <AlertTriangle className="h-8 w-8 text-destructive" />
                                            <div>
                                                <p className="font-bold text-destructive">Rechazo en Punto de Salida</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Se notificará al gerente y solicitante
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <Label className="input-label">
                                            Motivos de Rechazo <span className="text-destructive">*</span>
                                        </Label>
                                        <div className="space-y-2">
                                            {motivosRechazo.map((motivo) => (
                                                <div
                                                    key={motivo}
                                                    className={cn(
                                                        'flex items-center gap-3 p-3 rounded-sm border-2 cursor-pointer transition-colors',
                                                        motivosSeleccionados.includes(motivo)
                                                            ? 'border-destructive bg-destructive/10'
                                                            : 'border-border hover:border-muted-foreground'
                                                    )}
                                                    onClick={() => toggleMotivo(motivo)}
                                                >
                                                    <Checkbox
                                                        checked={motivosSeleccionados.includes(motivo)}
                                                        onCheckedChange={() => toggleMotivo(motivo)}
                                                    />
                                                    <span className="font-medium">{motivo}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="input-label">
                                            Observación <span className="text-destructive">*</span>
                                        </Label>
                                        <Textarea
                                            value={observacion}
                                            onChange={(e) => setObservacion(e.target.value)}
                                            placeholder="Describa el problema encontrado..."
                                            className="min-h-[100px] border-2"
                                            data-testid="observacion-rechazo-input"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </ScrollArea>

                    <DialogFooter className="pt-4 border-t">
                        <Button
                            variant="outline"
                            onClick={closeActionDialog}
                            className="border-2"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleDespacho}
                            disabled={processing}
                            className={`btn-industrial ${
                                actionType === 'despachar'
                                    ? 'bg-success text-success-foreground'
                                    : 'bg-destructive text-destructive-foreground'
                            }`}
                            data-testid="confirm-action-btn"
                        >
                            {processing ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : actionType === 'despachar' ? (
                                <>
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    Confirmar
                                </>
                            ) : (
                                <>
                                    <XCircle className="h-5 w-5 mr-2" />
                                    Rechazar
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
